import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import { SectionTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import FieldTable, { type DocumentRow } from '@/features/CsvImport/FieldTable';
import UploadPreview, {
  type SimpleCol,
  type SimpleRow,
  UPLOAD_STATE,
} from '@/features/CsvImport/UploadPreview';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useToastStore from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

const ESSENTIALFIELDS = ['account_name'];

type Props = {
  closeUploadModal: () => void;
};

type FieldDocData = SimpleCol & DocumentRow;

const columns: FieldDocData[] = [
  {
    columnKey: 'account_name',
    colWidth: 400,
    required: true,
    type: 'string',
    desc: 'The name of the Account.',
  },
  {
    columnKey: 'active_status',
    colWidth: 160,
    type: 'string',
    desc: "The company's sales, prospecting, or outreach status. input option : {churned | active}",
  },
  {
    columnKey: 'size',
    colWidth: 140,
    type: 'string',
    desc: 'The size of the company (e.g., small, large, enterprise or other).',
  },
  {
    columnKey: 'industry',
    colWidth: 200,
    type: 'string',
    desc: 'The type of business the company performs.',
  },
  {
    columnKey: 'plan',
    colWidth: 200,
    type: 'string',
    desc: 'The spending tier or subscription level (e.g., Basic, Pro, or Enterprise).',
  },
  {
    columnKey: 'rating',
    colWidth: 100,
    type: 'string',
    desc: 'The assigned qualitative label to the company (e.g., high, low, priority).',
  },
  {
    columnKey: 'type',
    colWidth: 200,
    type: 'string',
    desc: "The company's relationship to you (e.g., partner, reseller, vendor, or other).",
  },
  {
    columnKey: 'monthly_spend',
    colWidth: 200,
    type: 'string',
    desc: 'The total monthly value of deals closed with the company.',
  },
  {
    columnKey: 'employees',
    colWidth: 140,
    type: 'string',
    desc: 'A number of people employed by the account.',
  },
  {
    columnKey: 'annual_revenue',
    colWidth: 200,
    type: 'string',
    desc: 'The actual or estimated annual revenue of the company.',
  },
  {
    columnKey: 'agent_email',
    colWidth: 140,
    type: 'string',
    desc: 'The email of the agent.',
  },
];

const CompanyUploadBody = (props: Props) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const [errorMsg, setErrorMsg] = useState('');
  const [mountComplete, setMountComplete] = useState(false);
  const [uploading, setUploading] = useState<UPLOAD_STATE>(UPLOAD_STATE.READY);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState({
    current: 1,
    total: 100,
  });
  const [formattedData, setFormattedData] = useState<csvCompanyParams[]>([]);

  const onComplete = () => {
    setMountComplete(true);
    addDefaultToast('Successfully Completed', 'success');
  };

  const onFail = () => {
    setMountComplete(false);
    addDefaultToast('Upload Fail', 'fail');
  };

  const uploadData = async (data: csvCompanyParams[], fileFields: string[]) => {
    try {
      ESSENTIALFIELDS.forEach((field) => {
        if (!fileFields?.includes(field)) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        }
      });

      setFormattedData(data);

      setErrorMsg('');
      onComplete();
    } catch (e: any) {
      setErrorMsg(e.message);
      onFail();
    }
  };

  const confirmUpload = async () => {
    setUploading(UPLOAD_STATE.UPLOADING);
    setProgress((state) => ({
      ...state,
      total: formattedData.length,
    }));
    try {
      const chunkSize = 100;
      for (let i = 0; i < formattedData.length; i += chunkSize) {
        const chunk = formattedData.slice(i, i + chunkSize);
        await service.account.uploadCompanies(chunk);
        setProgress((state) => ({
          ...state,
          current: state.current + chunkSize,
        }));
      }

      setUploading(UPLOAD_STATE.COMPLETE);

      // Track company upload complete event
      track(AMP_KEYS.COMPANY_UPLOAD_COMPLETE, {
        company_count: formattedData.length,
        upload_type: 'csv',
      });

      addDefaultToast('The file has been successfully uploaded', 'success');
    } catch (e) {
      console.error(e);
      setUploading(UPLOAD_STATE.READY);
      addDefaultToast('Upload Fail', 'fail');
    }
  };

  const previewData: SimpleRow[] = useMemo(() => {
    return formattedData.slice(0, 100).map((row, index) => {
      return {
        rowKey: index,
        rowData: Object.entries(row).map(([key, value]) => {
          return {
            columnKey: key,
            rawData: value?.toString() || '',
          };
        }),
      };
    });
  }, [formattedData]);

  if (mountComplete)
    return (
      <UploadPreview
        columns={columns}
        total={formattedData.length}
        datas={previewData}
        progress={progress}
        uploading={uploading}
        confirmUpload={confirmUpload}
        closeUploadModal={props.closeUploadModal}
      />
    );

  return (
    <ScrollView style={{ height: '100%' }}>
      <Wrapper g={20}>
        {errorMsg && (
          <Typo weight="sb" color={color.red600} style={{ height: 24 }}>
            {errorMsg}
          </Typo>
        )}
        <VFlex
          g={16}
          padding="20px 24px"
          style={{ backgroundColor: color.gray200, borderRadius: 8 }}
        >
          <Typo type="body2" color={color.gray700}>
            You can upload your accounts using a csv or excel file.
            <br />
            See the table below for information on the account fields you can
            upload.
          </Typo>
          <a
            href="/assets/sample_account.csv"
            download="Syncly Account CSV import Sample.csv"
            target="_self"
            style={{ width: 'fit-content' }}
          >
            <Button
              variant="outlined"
              sizeType="m"
              style={{ width: 'fit-content' }}
            >
              Download sample CSV
            </Button>
          </a>
        </VFlex>
        <DnDFileUploader
          uploadData={uploadData}
          dragActive={dragActive}
          setDragActive={setDragActive}
        />
        <VFlex g={8}>
          <SectionTitle>Account field description</SectionTitle>
          <FieldTable rows={columns} />
        </VFlex>
      </Wrapper>
    </ScrollView>
  );
};

export default CompanyUploadBody;

const Wrapper = styled(VFlex)`
  padding: 36px 32px;
`;
